import type { Routes } from "shared/react";
import { StrictMode, Suspense } from "react";
import { useStore } from "shelving/react";
import { Catcher, Dialogs, ERROR_PAGE, LOADING_PAGE, MISSING_PAGE, MetadataContext, Navigator, NavigatorIsolation, NavigatorMetadata, NavigatorRoute, Notices, PRIVATE_PAGE } from "shared/react";
import { TestPage } from "./TestPage.js";
import { LoginPage } from "./account/LoginPage.js";
import { PasswordResetPage } from "./account/PasswordResetPage.js";
import { RegisterPage } from "./account/RegisterPage.js";
import { ChallengePage } from "./challenge/ChallengePage.js";
import { ChallengesPage } from "./challenge/ChallengesPage.js";
import { CommentPage } from "./comment/CommentPage.js";
import { config } from "./config.js";
import { CounterWatcher } from "./counter/CounterWatcher.js";
import { ChallengeEditPage } from "./create/ChallengeEditPage.js";
import { ListEditPage } from "./create/ListEditPage.js";
import { NoteEditPage } from "./create/NoteEditPage.js";
import { RewardEditPage } from "./create/RewardEditPage.js";
import { GroupCreatePage } from "./group/GroupCreatePage.js";
import { GroupRouter } from "./group/GroupRouter.js";
import { GroupsPage } from "./group/GroupsPage.js";
import { InvitesPage } from "./group/InvitesPage.js";
import { MessagePage } from "./group/MessagePage.js";
import { LeaderboardPage } from "./leaderboard/LeaderboardPage.js";
import { LeaderboardsPage } from "./leaderboard/LeaderboardsPage.js";
import { ListPage } from "./list/ListPage.js";
import { MenuPage } from "./misc/MenuPage.js";
import { Navigation } from "./misc/Navigation.js";
import { UsernameRouter } from "./misc/UsernameRouter.js";
import { NotePage } from "./note/NotePage.js";
import { NotificationsPage } from "./notification/NotificationsPage.js";
import { PostCreatePage } from "./post/PostCreatePage.js";
import { PostPage } from "./post/PostPage.js";
import { PostsPage } from "./post/PostsPage.js";
import { MyProfileRouter, ProfileRouter } from "./profile/ProfileRouter.js";
import { RequestsPage } from "./profile/RequestsPage.js";
import { ReactionsPage } from "./reaction/ReactionsPage.js";
import { ReactorsPage } from "./reaction/ReactorsPage.js";
import { RewardPage } from "./reward/RewardPage.js";
import { SearchPage } from "./search/SearchPage.js";
import { PrivacySettingsPage } from "./settings/PrivacySettingsPage.js";
import { ProfileEditPage } from "./settings/ProfileEditPage.js";
import { SettingsPage } from "./settings/SettingsPage.js";
import { HashTagPage } from "./tag/HashTagPage.js";
import { DataContext } from "./util/db.js";
import { USER } from "./util/user.js";

const IN_ROUTES: Routes = {
	"/test": TestPage,

	// Challenges.
	"/challenges": ChallengesPage,
	"/challenges/new": "/me/challenges/new",
	"/challenges/{status}": ChallengesPage,
	"/challenge": "/challenges",
	"/challenge/{id}/edit": ChallengeEditPage,
	"/challenge/{id}": ChallengePage,

	// Leaderboards.
	"/leaderboard/{type}/{id}/{cid}": LeaderboardPage,
	"/leaderboard/{type}/{id}": LeaderboardsPage,

	// Notes.
	"/note/{id}/edit": NoteEditPage,
	"/note/{id}": NotePage,

	// Lists.
	"/list/{id}/edit": ListEditPage,
	"/list/{id}": ListPage,

	// Rewards.
	"/rewards": "/me/rewards",
	"/rewards/new": "/me/rewards/new",
	"/reward/{id}/edit": RewardEditPage,
	"/reward/{id}": RewardPage,
	"/badges": "/me/badges",
	"/badges/new": "/me/badges/new",

	// Groups.
	"/groups": GroupsPage,
	"/groups/new": GroupCreatePage,
	"/group/{gid}/{slug}": GroupRouter,
	"/group/{gid}": GroupRouter,
	"/invites": InvitesPage,

	// Messages.
	"/message/{id}": MessagePage,

	// Posts.
	"/posts": PostsPage,
	"/posts/new": PostCreatePage,
	"/post/{id}": PostPage,

	// Comments.
	"/comment/{id}": CommentPage,

	// Hash tags.
	"/tag/{slug}": HashTagPage,

	// Search.
	"/search": SearchPage,
	"/search/{type}": SearchPage,

	// Settings.
	"/settings": SettingsPage,
	"/settings/profile": ProfileEditPage,
	"/settings/privacy": PrivacySettingsPage,

	// Misc.
	"/menu": MenuPage,
	"/notifications": NotificationsPage,

	// Profiles.
	"/profile/{uid}/{slug}": ProfileRouter,
	"/profile/{uid}": ProfileRouter,
	"/me": MyProfileRouter,
	"/me/{slug}": MyProfileRouter,
	"/requests": RequestsPage,

	// Reactions.
	"/reactions/{type}/{id}/{filter}": ReactorsPage,
	"/reactions/{type}/{id}": ReactionsPage,

	// Usernames.
	"/{username}/{slug}": UsernameRouter,
	"/{username}": UsernameRouter,

	// Home.
	"/": "/posts",
};

const OUT_ROUTES: Routes = {
	"/test": TestPage,
	"/reset": PasswordResetPage,
	"/register": RegisterPage,
	"/login": LoginPage,
	"/*": LoginPage, // Every path shows login.
	"/": LoginPage,
};

export function App(): React.ReactElement {
	return (
		<StrictMode>
			<MetadataContext {...config}>
				<Catcher error={ERROR_PAGE} forbidden={PRIVATE_PAGE} required={MISSING_PAGE}>
					<DataContext>
						<Suspense fallback={LOADING_PAGE}>
							<AppPage />
							<Notices />
						</Suspense>
					</DataContext>
				</Catcher>
			</MetadataContext>
		</StrictMode>
	);
}

function AppPage(): React.ReactElement {
	// Reload the page whenever the user state or current page changes.
	const user = useStore(USER).value;

	// <Navigator> selects route based on browser URL (`key=""` remounts everything if a different user logs in).
	// <NavigatorIsolation> remounts <Catcher> and <DataContext> on navigation, so errors are cleared if the user changes page.
	// <Catcher> catches errors in the current navigator content.
	// <DialogProvider> allows the current navigator content to use dialogs.
	// <DialogContext> allows the current navigator content to use data.
	// <Suspense> shows a loading page when the current navigator content is loading data.
	return (
		<Navigator key={user?.uid || "none"} routes={user ? IN_ROUTES : OUT_ROUTES}>
			<NavigatorIsolation>
				<Catcher error={ERROR_PAGE} forbidden={PRIVATE_PAGE} required={MISSING_PAGE}>
					<DataContext>
						<Dialogs>
							<Suspense fallback={LOADING_PAGE}>
								<NavigatorRoute />
							</Suspense>
						</Dialogs>
					</DataContext>
				</Catcher>
			</NavigatorIsolation>
			{user ? (
				<NavigatorMetadata>
					<CounterWatcher uid={user.uid} type="points" />
					<CounterWatcher uid={user.uid} type="love" />
					<Navigation />
				</NavigatorMetadata>
			) : null}
		</Navigator>
	);
}
